import {zodResolver} from '@hookform/resolvers/zod';
import {isFrozenTaskDate} from '@nursio-link/common/utils';
import {Repeat2} from 'lucide-react';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {InputLabel} from '@/components/ui/input';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import type {Task} from '@/services/graphqlHooks';
import {
  useDeleteTaskMutation,
  useFindRecurringTaskByIdQuery,
  useGetTaskQuery,
  useRejectTaskMutation,
  useUpdateTaskMutation,
} from '@/services/graphqlHooks';

import type {
  RejectTaskFormValues,
  TaskFormValues,
  TaskFromDefaultValueInput,
} from '../../utils/tasksFormInputs';
import {
  mapTaskFormValuesToTaskInput,
  rejectTaskFormSchema,
  StandardTaskVariant,
  taskFormDefaultValues,
  taskFormSchema,
} from '../../utils/tasksFormInputs';
import {isStandardTask} from '../../utils/tasksHelpers';
import ArchivedTaskNotice from '../ArchivedTaskNotice';
import TaskFormFields from '../TaskFormFields';
import TaskFormPreview from '../TaskFormPreview';

export type EditTaskDialogProps = DialogProps;

const EditTaskDialog = ({onOpenChange, ...props}: EditTaskDialogProps) => {
  const {t} = useTranslation();
  const {confirm} = useConfirm();
  const {toast} = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('task') || '';

  const deleteTask = useDeleteTaskMutation(graphqlClient);
  const updateTask = useUpdateTaskMutation(graphqlClient);
  const rejectTask = useRejectTaskMutation(graphqlClient);

  const isDisabled =
    deleteTask.isPending || updateTask.isPending || rejectTask.isPending;

  const [isReject, setIsReject] = useState(false);
  const [recurringTaskExist, setRecurringTaskExist] = useState<boolean>(false);

  const {data: taskData, isLoading: isTaskLoading} = useGetTaskQuery(
    graphqlClient,
    {id},
    {
      enabled: !!(id && props.open),
      staleTime: 0,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const task = taskData?.getTask as Task;

  const {data: parentRecurringTask} = useFindRecurringTaskByIdQuery(
    graphqlClient,
    {id: task?.parentRecurringTaskId || ''},
    {
      staleTime: 0,
      enabled: !!task?.parentRecurringTaskId,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  useEffect(() => {
    setRecurringTaskExist(!!parentRecurringTask?.findRecurringTaskById);
  }, [parentRecurringTask]);

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.OneTime,
    },
  });

  const rejectForm = useForm<RejectTaskFormValues>({
    resolver: zodResolver(rejectTaskFormSchema),
    defaultValues: {rejectionNote: ''},
  });

  useEffect(() => {
    if (!task) return;
    form.reset({
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.OneTime,
    });
    form.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  const handleSubmit = async (values: TaskFormValues) => {
    try {
      await updateTask.mutateAsync({
        input: {id, ...mapTaskFormValuesToTaskInput(values)},
      });
      toast({
        title: t('Task updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleReject = async (values: RejectTaskFormValues) => {
    try {
      await rejectTask.mutateAsync({
        input: {id, rejectionNote: values.rejectionNote || ''},
      });
      toast({
        title: t('Task rejected'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete task'),
        message: t('Are you sure you want to delete this task?'),
      });
      await deleteTask.mutateAsync({id});
      toast({
        title: t('Task deleted'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const isArchived = isFrozenTaskDate(task?.date);
  const isReadOnly = !!task?.completedBy || isArchived;
  const showRejectionNote =
    isStandardTask(task) && !task?.completedBy && !isArchived;

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form
            onSubmit={
              isReject
                ? rejectForm.handleSubmit(handleReject)
                : form.handleSubmit(handleSubmit)
            }
            noValidate
          >
            <DialogHeader className="md:hidden">
              <DialogTitle>{t('Task')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="py-0">
              {isTaskLoading && <FormSkeleton />}
              {!isTaskLoading && !task && <div>{t('Task not found')}</div>}

              {task && !isReadOnly && (
                <TaskFormFields form={form} hideTaskType />
              )}
              {task && isReadOnly && <TaskFormPreview task={task} />}
              {task && isArchived && <ArchivedTaskNotice />}

              {showRejectionNote && (
                <Form {...rejectForm}>
                  <div className="mt-10 flex flex-col gap-10">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="isReject"
                        checked={isReject}
                        onCheckedChange={val => {
                          setIsReject(val as boolean);
                        }}
                      />
                      <InputLabel htmlFor="isReject" className="text-red-600">
                        {t('Reject task')}
                      </InputLabel>
                    </div>
                    {isReject && (
                      <FormField
                        control={rejectForm.control}
                        name="rejectionNote"
                        render={({field}) => (
                          <FormItem>
                            <FormLabel>{t('Rejection note')}</FormLabel>
                            <FormControl>
                              <Textarea {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </Form>
              )}

              {task?.parentRecurringTaskId && (
                <div className="m-2">
                  <Button
                    type="button"
                    disabled={!recurringTaskExist}
                    className="flex flex-row gap-2"
                    variant="link"
                    size="text"
                    onClick={() => {
                      searchParams.append(
                        'recurringTask',
                        task.parentRecurringTaskId!
                      );
                      setSearchParams(searchParams);
                    }}
                  >
                    <Repeat2 size="16" />{' '}
                    {recurringTaskExist
                      ? t('Edit recurring task')
                      : t('Recurring task deleted')}
                  </Button>
                </div>
              )}
            </DialogSection>
            <DialogFooter>
              {!!task && !isReadOnly && !isReject && (
                <>
                  <Button
                    type="button"
                    className="sm:mr-auto"
                    variant="warning"
                    onClick={() => handleDelete()}
                    disabled={isDisabled}
                  >
                    {t('Delete task')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isDisabled}>
                    {t('Submit')}
                  </Button>
                </>
              )}
              {!!task && !isReadOnly && isReject && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isDisabled}>
                    {t('Submit')}
                  </Button>
                </>
              )}
              {(!task || isReadOnly) && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default EditTaskDialog;
